/**
 * PD-16863 — the page a visitor was trying to open when the storefront sent
 * them to sign in (or to pick an account) first, so they land on it after.
 *
 * A text links straight to a page, such as `/statements/42` or `/support/9`.
 * A signed-out visitor used to be sent to Home, and a customer with several
 * accounts to the account picker, and the page they came for was lost. The
 * path is kept in sessionStorage (this tab only, gone when it closes), read
 * once, and only a same-origin path that is not itself part of signing in is
 * ever kept or returned. It also expires, so a sign-in long after an
 * abandoned link does not jump somewhere unexpected.
 *
 * No workspace imports: the storefront's unit tests run without path aliases.
 */

const STORAGE_KEY = 'portal.returnTo';

/** How long a remembered page stays worth returning to. */
export const RETURN_PATH_MAX_AGE_MS = 30 * 60 * 1000;

/** Paths that are part of signing in (or work without it): never a destination. */
const EXCLUDED_PATHS: ReadonlyArray<string> = [
	'/',
	'/login',
	'/select-client',
	'/register',
	'/forgot-password',
	'/reset-password',
	'/set-password',
	'/sign-in-link',
	'/account-deleted',
	'/app',
];

/** Path prefixes for the public payment links, which need no sign-in. */
const EXCLUDED_PREFIXES: ReadonlyArray<string> = ['/pay/', '/payment-method/'];

/** Any origin works: it only lets URL resolve the path, and must not change. */
const PROBE_ORIGIN = 'https://storefront.invalid';

/** The part of Storage this module uses, so tests can pass their own. */
export interface ReturnPathStorageModel {
	getItem: (key: string) => string | null;
	setItem: (key: string, value: string) => void;
	removeItem: (key: string) => void;
}

/**
 * `raw` as a same-origin path (pathname, query and hash) worth returning to,
 * else null. Refuses anything that could leave the storefront: an absolute or
 * protocol-relative URL (`//host`), a backslash (browsers read `/\host` as
 * `//host`), and control characters (URL parsing strips a tab or newline, so
 * `/\t/host` would become `//host`).
 */
export function sanitizeReturnPath(raw: unknown): string | null {
	if (typeof raw !== 'string') {
		return null;
	}
	const value = raw.trim();
	if (
		value === '' ||
		value.length > 2048 ||
		!value.startsWith('/') ||
		value.startsWith('//') ||
		/[\u0000-\u001f\u007f\\]/.test(value)
	) {
		return null;
	}

	let url: URL;
	try {
		url = new URL(value, PROBE_ORIGIN);
	} catch {
		return null;
	}
	if (url.origin !== PROBE_ORIGIN) {
		return null;
	}

	const trimmedPath = url.pathname.replace(/\/+$/, '');
	const pathname = trimmedPath === '' ? '/' : trimmedPath;
	if (
		EXCLUDED_PATHS.includes(pathname) ||
		EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix))
	) {
		return null;
	}
	return `${url.pathname}${url.search}${url.hash}`;
}

function sessionStore(): ReturnPathStorageModel | null {
	try {
		return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
	} catch {
		// Storage can throw when the browser blocks it; the visitor then
		// simply lands on the start page, as before.
		return null;
	}
}

/** Remember `path` to return to after sign-in; ignored when it is not a safe destination. */
export function rememberReturnPath(
	path: string,
	storage: ReturnPathStorageModel | null = sessionStore(),
	now: number = Date.now(),
): void {
	const safe = sanitizeReturnPath(path);
	if (safe == null || storage == null) {
		return;
	}
	try {
		storage.setItem(STORAGE_KEY, JSON.stringify({ path: safe, savedAt: now }));
	} catch {
		// Blocked or full storage: nothing to return to later.
	}
}

/**
 * The remembered path, cleared as it is read (one-shot), or null when there
 * is none, it has expired, or it is not a safe destination.
 */
export function takeReturnPath(
	storage: ReturnPathStorageModel | null = sessionStore(),
	now: number = Date.now(),
): string | null {
	if (storage == null) {
		return null;
	}
	let raw: string | null;
	try {
		raw = storage.getItem(STORAGE_KEY);
		storage.removeItem(STORAGE_KEY);
	} catch {
		return null;
	}
	if (raw == null) {
		return null;
	}

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return null;
	}
	if (parsed == null || typeof parsed !== 'object') {
		return null;
	}
	const entry = parsed as Record<string, unknown>;
	const savedAt = entry['savedAt'];
	if (
		typeof savedAt !== 'number' ||
		savedAt > now ||
		now - savedAt > RETURN_PATH_MAX_AGE_MS
	) {
		return null;
	}
	return sanitizeReturnPath(entry['path']);
}
